import { TrendingData } from "@/pages/caster/data-entry/models/caster.models";
import { useTheme } from "@mui/material/styles";
import { dataEntryTableStyles } from "../styles/dataEntryTableStyles";
import { dxtToLocalServerTime } from "@dexteel/mesf-core";
import { Box, Paper, Typography } from "@mui/material";
import ReactECharts from "echarts-for-react";
import { useEffect, useMemo, useRef, useState } from "react";

interface ProcessedTrendingData {
  Datetime: string;
  Value: number;
}

interface SingleTrendChartProps {
  title: string;
  data: TrendingData[];
  valueUnit?: string;
}

export const SingleLineChart = ({
  title,
  data,
  valueUnit = "°F",
}: SingleTrendChartProps) => {
  const theme = useTheme();
  const styles = dataEntryTableStyles(theme);
  const echartsRef = useRef<ReactECharts>(null);

  const [lastValue, setLastValue] = useState<ProcessedTrendingData | null>(
    null,
  );

  const getChartOptions = useMemo(() => {
    const processedData: ProcessedTrendingData[] =
      data
        ?.filter(
          (item): item is { Datetime: Date; Value: number } =>
            item.Datetime !== null && item.Value !== null,
        )
        .map((item) => ({
          Datetime: dxtToLocalServerTime(item.Datetime, "yyyy-MM-dd HH:mm:ss"),
          Value: item.Value,
        })) || [];

    if (processedData.length === 0) {
      return {};
    }

    setLastValue(processedData[processedData.length - 1]);

    const values = processedData.map((item) => item.Value);
    const minValue = Math.min(...values);
    const maxValue = Math.max(...values);

    const margin = Math.ceil((maxValue - minValue) * 0.1);
    const yMin = Math.max(0, Math.floor(minValue) - margin);
    const yMax = Math.ceil(maxValue + margin);

    const timestamps = processedData.map((item) =>
      new Date(item.Datetime).getTime(),
    );
    const minTime = Math.min(...timestamps);
    const maxTime = Math.max(...timestamps);

    return {
      animation: false,
      toolbox: {
        feature: {
          saveAsImage: {},
        },
      },
      dataZoom: [
        {
          type: "inside",
          show: true,
        },
      ],
      tooltip: {
        trigger: "axis",
        formatter: (params: any) => {
          const datetime = new Date(params[0].value[0]).toLocaleTimeString(
            "en-US",
            {
              hour12: false,
              hour: "2-digit",
              minute: "2-digit",
              second: "2-digit",
            },
          );
          return `Time: ${datetime}<br>Value: ${params[0].value[1].toFixed(2)} ${valueUnit}`;
        },
      },
      xAxis: {
        type: "time",
        min: minTime,
        max: maxTime,
        axisLabel: {
          formatter: (value: number) => {
            return new Date(value).toLocaleTimeString("en-US", {
              hour12: false,
              hour: "2-digit",
              minute: "2-digit",
            });
          },
        },
        axisTick: {
          alignWithLabel: true,
        },
        splitNumber: 4, // Results in 5 ticks
      },
      yAxis: {
        type: "value",
        name: valueUnit,
        nameTextStyle: {
          fontSize: 14,
          align: "left",
        },
        nameLocation: "end",
        min: yMin,
        max: yMax,
      },
      series: [
        {
          type: "line",
          smooth: true,
          symbol: "none",
          sampling: "lttb",
          itemStyle: {
            color: "#1f77b4",
          },
          data: processedData.map((item) => [
            new Date(item.Datetime).getTime(),
            item.Value,
          ]),
        },
      ],
    };
  }, [data, valueUnit]);

  useEffect(() => {
    if (echartsRef.current) {
      const instance = echartsRef.current.getEchartsInstance();
      instance.setOption(getChartOptions, {
        replaceMerge: ["series"],
        lazyUpdate: true,
      });
    }
  }, [getChartOptions]);

  return (
    <Paper>
      <Box padding={1} sx={styles.agGridBox}>
        <Box sx={{ position: "relative" }}>
          <Box
            sx={{
              backgroundColor: (theme) => theme.palette.common.white,
              borderBottom: (theme) => `1px solid ${theme.palette.divider}`,
              padding: (theme) => theme.spacing(1),
              paddingLeft: (theme) => theme.spacing(3),
              fontWeight: 600,
              textAlign: "center",
              fontSize: "12px",
              color: (theme) => theme.palette.text.primary,
              fontFamily:
                '"Open Sans", "Lucida Grande", Arial, Verdana, Helvetica, sans-serif',
            }}
          >
            {title}
          </Box>

          <Typography
            variant="body2"
            sx={{
              position: "absolute",
              top: "40px",
              right: "30px",
              zIndex: 100,
              color: "#1f77b4",
              fontWeight: "bold",
            }}
          >
            {lastValue ? `${lastValue?.Value.toFixed(2)} ${valueUnit}` : ""}
          </Typography>

          <ReactECharts
            ref={echartsRef}
            option={getChartOptions}
            notMerge={false}
          />
        </Box>
      </Box>
    </Paper>
  );
};
